import { useCallback, useEffect, useRef, useState } from 'react';

import { useMenuSelection } from 'global/MenuSelectionContext';
import { logger } from 'utils/logger';
import { useIsMobile } from 'utils/mediaQueries';

// Hook import (always imports, but may fail if context is not available)

interface SmartReloadOptions {
  minInterval?: number; // Minimum interval between reloads (ms)
  meetingProtection?: boolean; // Whether to protect meetings
  checkInterval?: number; // Interval to check for meetings (ms)
  enableMenuDetection?: boolean; // Whether to use menu detection (default: true)
}

// Wrapper to capture hook errors
const useMenuSelectionSafe = () => {
  try {
    return useMenuSelection();
  } catch {
    logger.debug('[SmartReload] MenuSelectionContext not available, using basic detection');
    return {
      thirdLevelMenu: null,
      secondLevelMenu: null,
      firstLevelMenu: null,
      currentLevel: null,
    };
  }
};

export const useSmartReload = (options: SmartReloadOptions = {}) => {
  const {
    minInterval = 120000, // 2 minutes default
    meetingProtection = true,
    checkInterval = 5000, // 5 seconds
    enableMenuDetection = true,
  } = options;

  const [isInMeeting, setIsInMeeting] = useState(false);
  const [lastReloadTime, setLastReloadTime] = useState(0);
  const [reloadCount, setReloadCount] = useState(0);
  const [isReloadBlocked, setIsReloadBlocked] = useState(false);
  const lastReloadReason = useRef<string>('');

  // Hook to detect mobile
  const isMobile = useIsMobile();

  // Always call the hook (now in a separate wrapper)
  const menuContext = useMenuSelectionSafe();

  // Extract menu data safely
  const thirdLevelMenu = enableMenuDetection ? menuContext?.thirdLevelMenu || null : null;
  const secondLevelMenu = enableMenuDetection ? menuContext?.secondLevelMenu || null : null;
  const firstLevelMenu = enableMenuDetection ? menuContext?.firstLevelMenu || null : null;
  const currentLevel = enableMenuDetection ? menuContext?.currentLevel || null : null;

  // Detect if in a Jitsi meeting
  useEffect(() => {
    if (!meetingProtection) return;

    const checkIfInMeeting = () => {
      try {
        // 1. Check if Jitsi iframe exists (visual detection)
        const jitsiFrame = document.querySelector('iframe[id^="jitsiConferenceFrame"]');
        const isInJitsi = !!jitsiFrame;

        // 2. Check if there are Jitsi-specific elements
        const hasJitsiElements =
          document.querySelector('.jitsi-iframe') ||
          document.querySelector('[data-jitsi]') ||
          document.querySelector('.JaaSMeeting') ||
          document.querySelector('[class*="jitsi"]') ||
          document.querySelector('[class*="JaaSMeeting"]');

        // 3. Check if the current menu is a meeting menu (detection by dynamic_component)
        const currentMenu = thirdLevelMenu || secondLevelMenu || firstLevelMenu;
        const isMeetingMenu =
          (enableMenuDetection && currentMenu?.feature?.dynamic_component === 'MEETING_ROOM') ||
          (enableMenuDetection && currentMenu?.feature?.dynamic_component === 'MEETING_ROOM_ROOM');

        // 4. Check if on a meeting page (URL)
        const isInMeetingPage =
          window.location.pathname.includes('/rooms/') ||
          window.location.pathname.includes('/meeting/');

        // 5. Check if there are active Jitsi components
        const hasActiveJitsiComponent =
          document.querySelector('[class*="JaaSMeeting"]') ||
          document.querySelector('[class*="jitsi"]') ||
          document.querySelector('iframe[src*="jitsi"]') ||
          document.querySelector('div[class*="jitsi"]');

        // 6. Check if there are fullscreen Jitsi components (mobile)
        const hasFullscreenJitsi =
          isMobile &&
          (document.querySelector('[style*="position: fixed"]') ||
            document.querySelector('[style*="100vw"]') ||
            document.querySelector('[style*="100vh"]'));

        // Combine all checks
        const meetingDetected =
          isInJitsi ||
          isMeetingMenu ||
          isInMeetingPage ||
          !!hasJitsiElements ||
          !!hasActiveJitsiComponent ||
          !!hasFullscreenJitsi;

        if (meetingDetected !== isInMeeting) {
          logger.debug('[SmartReload] Meeting status changed:', {
            from: isInMeeting,
            to: meetingDetected,
            isMobile,
            enableMenuDetection,
            reason: {
              jitsiFrame: !!jitsiFrame,
              isMeetingMenu,
              meetingPage: isInMeetingPage,
              jitsiElements: !!hasJitsiElements,
              activeJitsiComponent: !!hasActiveJitsiComponent,
              fullscreenJitsi: !!hasFullscreenJitsi,
              currentMenu: currentMenu?.label,
              dynamicComponent: currentMenu?.feature?.dynamic_component,
              currentLevel,
            },
          });
          setIsInMeeting(meetingDetected);
        }
      } catch (error) {
        logger.error('[SmartReload] Error checking meeting:', error);
      }
    };

    // Check immediately
    checkIfInMeeting();

    // Check periodically
    const interval = setInterval(checkIfInMeeting, checkInterval);
    return () => clearInterval(interval);
  }, [
    meetingProtection,
    checkInterval,
    isInMeeting,
    thirdLevelMenu,
    secondLevelMenu,
    firstLevelMenu,
    currentLevel,
    isMobile,
    enableMenuDetection,
  ]);

  // Main function to handle theme issues
  const handleThemeIssue = useCallback(
    (reason: string = 'tema') => {
      const now = Date.now();
      const timeSinceLastReload = now - lastReloadTime;

      // Detailed log for debugging
      logger.debug('[SmartReload] Checking reload:', {
        reason,
        isInMeeting,
        isMobile,
        enableMenuDetection,
        timeSinceLastReload,
        minInterval,
        lastReloadTime,
        reloadCount,
        isReloadBlocked,
        currentMenu: thirdLevelMenu || secondLevelMenu || firstLevelMenu,
        dynamicComponent: (thirdLevelMenu || secondLevelMenu || firstLevelMenu)?.feature
          ?.dynamic_component,
      });

      // Do not reload if:
      // 1. In a meeting (critical protection)
      if (isInMeeting && meetingProtection) {
        logger.warn('[SmartReload] Reload BLOCKED: User in Jitsi meeting', {
          reason,
          isInMeeting,
          isMobile,
          enableMenuDetection,
          currentMenu: (thirdLevelMenu || secondLevelMenu || firstLevelMenu)?.label,
          dynamicComponent: (thirdLevelMenu || secondLevelMenu || firstLevelMenu)?.feature
            ?.dynamic_component,
        });
        setIsReloadBlocked(true);
        lastReloadReason.current = reason;

        // Dispatch event to notify other components
        window.dispatchEvent(
          new CustomEvent('reloadBlocked', {
            detail: { reason, isInMeeting, isMobile },
          })
        );
        return;
      }

      // 2. Reloaded too recently
      if (timeSinceLastReload < minInterval) {
        logger.warn('[SmartReload] Reload BLOCKED: Interval too short', {
          timeSinceLastReload,
          minInterval,
          isMobile,
        });
        setIsReloadBlocked(true);
        lastReloadReason.current = reason;
        return;
      }

      // 3. First time (may be normal loading)
      if (lastReloadTime === 0) {
        logger.debug('[SmartReload] First check, not reloading', {
          isMobile,
          enableMenuDetection,
        });
        setLastReloadTime(now);
        return;
      }

      // 4. Too many reloads in sequence (loop protection)
      if (reloadCount > 3) {
        logger.error('[SmartReload] Too many reloads, blocking to prevent loop', {
          isMobile,
          enableMenuDetection,
        });
        setIsReloadBlocked(true);
        lastReloadReason.current = 'too_many_reloads';
        return;
      }

      // If we got here, can reload
      logger.debug('[SmartReload] Reloading application:', {
        reason,
        reloadCount: reloadCount + 1,
        timeSinceLastReload,
        isMobile,
        enableMenuDetection,
        currentMenu: (thirdLevelMenu || secondLevelMenu || firstLevelMenu)?.label,
      });

      setLastReloadTime(now);
      setReloadCount(prev => prev + 1);
      setIsReloadBlocked(false);
      lastReloadReason.current = '';

      // Short delay to allow logs
      setTimeout(() => {
        window.location.reload();
      }, 100);
    },
    [
      isInMeeting,
      meetingProtection,
      lastReloadTime,
      minInterval,
      reloadCount,
      thirdLevelMenu,
      secondLevelMenu,
      firstLevelMenu,
      isMobile,
      enableMenuDetection,
      isReloadBlocked,
    ]
  );

  // Function to force reload (used by user)
  const forceReload = useCallback(() => {
    logger.debug('[SmartReload] Forced reload by user', {
      isMobile,
      enableMenuDetection,
    });
    setLastReloadTime(Date.now());
    setReloadCount(0);
    setIsReloadBlocked(false);
    lastReloadReason.current = '';
    window.location.reload();
  }, [isMobile, enableMenuDetection]);

  // Function to reset counters
  const resetCounters = useCallback(() => {
    logger.debug('[SmartReload] Resetting counters', { isMobile, enableMenuDetection });
    setReloadCount(0);
    setIsReloadBlocked(false);
    lastReloadReason.current = '';
  }, [isMobile, enableMenuDetection]);

  // Function to check if reload is allowed
  const canReload = useCallback(() => {
    const now = Date.now();
    const timeSinceLastReload = now - lastReloadTime;

    return (
      !isInMeeting && timeSinceLastReload >= minInterval && reloadCount <= 3 && !isReloadBlocked
    );
  }, [isInMeeting, lastReloadTime, minInterval, reloadCount, isReloadBlocked]);

  // Save statistics to localStorage for debugging
  useEffect(() => {
    const stats = {
      lastReloadTime,
      reloadCount,
      isReloadBlocked,
      lastReloadReason: lastReloadReason.current,
      isInMeeting,
      isMobile,
      enableMenuDetection,
      currentMenu: (thirdLevelMenu || secondLevelMenu || firstLevelMenu)?.label,
      dynamicComponent: (thirdLevelMenu || secondLevelMenu || firstLevelMenu)?.feature
        ?.dynamic_component,
      currentLevel,
      timestamp: Date.now(),
    };

    try {
      localStorage.setItem('smart_reload_stats', JSON.stringify(stats));
    } catch (error) {
      logger.error('[SmartReload] Error saving statistics:', error);
    }
  }, [
    lastReloadTime,
    reloadCount,
    isReloadBlocked,
    isInMeeting,
    thirdLevelMenu,
    secondLevelMenu,
    firstLevelMenu,
    currentLevel,
    isMobile,
    enableMenuDetection,
  ]);

  // Load statistics from localStorage on initialization
  useEffect(() => {
    try {
      const savedStats = localStorage.getItem('smart_reload_stats');
      if (savedStats) {
        const stats = JSON.parse(savedStats);
        const now = Date.now();

        // Only load if not too old (1 hour)
        if (now - stats.timestamp < 60 * 60 * 1000) {
          setLastReloadTime(stats.lastReloadTime || 0);
          setReloadCount(stats.reloadCount || 0);
          setIsReloadBlocked(stats.isReloadBlocked || false);
          lastReloadReason.current = stats.lastReloadReason || '';

          logger.debug('[SmartReload] Statistics loaded:', { data: stats });
        }
      }
    } catch (error) {
      logger.error('[SmartReload] Error loading statistics:', error);
    }
  }, []);

  return {
    handleThemeIssue,
    forceReload,
    resetCounters,
    canReload,
    isInMeeting,
    isReloadBlocked,
    reloadCount,
    lastReloadReason: lastReloadReason.current,
    stats: {
      lastReloadTime,
      reloadCount,
      isReloadBlocked,
      isInMeeting,
      isMobile,
      enableMenuDetection,
      currentMenu: (thirdLevelMenu || secondLevelMenu || firstLevelMenu)?.label,
      dynamicComponent: (thirdLevelMenu || secondLevelMenu || firstLevelMenu)?.feature
        ?.dynamic_component,
      currentLevel,
    },
  };
};
